import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import imageCompression from 'browser-image-compression';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ClipboardCheck, Plus, Eye, Edit, Trash2, Download, FileText,
  ChevronLeft, ChevronRight, X, Camera, Upload, Search,
  CheckCircle2, AlertTriangle, AlertCircle, Calendar, Clock,
  MapPin, User, Building2, Filter, Pen, Send, Mail, BarChart3
} from 'lucide-react';
import ProduccionLayout from '@/components/ProduccionLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJETIVOS_OPTIONS = [
  'Entrega de documentos',
  'Recepción de documentos',
  'Revisión cumplimiento RSPO',
  'Inspección de campo',
  'Seguimiento de producción',
];

const INSPECCION_OPTIONS = [
  'Lotes en producción',
  'Áreas de cosecha',
  'Centro de acopio',
  'Caminos internos',
];

const ESTADO_LABELS = {
  conforme: 'Conforme',
  conforme_observaciones: 'Conforme con observaciones',
  requiere_acciones: 'Requiere acciones correctivas',
};

const ESTADO_COLORS = {
  conforme: 'bg-emerald-100 text-emerald-800',
  conforme_observaciones: 'bg-amber-100 text-amber-800',
  requiere_acciones: 'bg-red-100 text-red-800',
};

// ========== Signature Pad Component ==========
function SignaturePad({ label, value, onChange, testId }) {
  const sigRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);

  const handleClear = () => {
    if (sigRef.current) {
      sigRef.current.clear();
      setIsEmpty(true);
      onChange(null);
    }
  };

  const handleEnd = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      onChange(dataUrl);
      setIsEmpty(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {value && !sigRef.current ? (
        <div className="space-y-2">
          <div className="border rounded-lg p-2 bg-white">
            <img src={value} alt="Firma" className="max-h-24 mx-auto" />
          </div>
          <Button variant="outline" size="sm" onClick={() => onChange(null)} data-testid={`${testId}-clear`}>
            <Pen className="w-3 h-3 mr-1" /> Volver a firmar
          </Button>
        </div>
      ) : (
        <div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none" data-testid={testId}>
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{
                className: 'w-full',
                style: { width: '100%', height: '120px' },
              }}
              onEnd={handleEnd}
            />
          </div>
          <div className="flex gap-2 mt-1">
            <Button variant="ghost" size="sm" onClick={handleClear} data-testid={`${testId}-clear`}>
              <X className="w-3 h-3 mr-1" /> Limpiar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== Photo Preview Modal ==========
function PhotoPreviewModal({ photo, onClose, onReplace, onDelete, testId }) {
  if (!photo) return null;
  const imgSrc = photo.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo.url}` : photo.url;
  return (
    <Dialog open={!!photo} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Foto del documento</DialogTitle>
        </DialogHeader>
        <div className="flex justify-center bg-gray-50 rounded-lg p-2">
          <img src={imgSrc} alt="Foto documento" className="max-h-80 rounded object-contain" />
        </div>
        <DialogFooter className="flex gap-2 sm:gap-2">
          {onReplace && (
            <label className="cursor-pointer">
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onReplace} data-testid={`${testId}-replace-input`} />
              <Button variant="outline" size="sm" asChild><span><Camera className="w-4 h-4 mr-1" /> Cambiar</span></Button>
            </label>
          )}
          {onDelete && (
            <Button variant="destructive" size="sm" onClick={onDelete} data-testid={`${testId}-delete-photo`}>
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== Inline Photo Upload (for textareas: hallazgos, observaciones, acta fisica) ==========
function InlinePhotoUpload({ onUpload, uploading, photos, onRemove, testId }) {
  return (
    <div>
      {photos && photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1 mb-1">
          {photos.map((p, i) => (
            <div key={i} className="relative group w-14 h-14 rounded border overflow-hidden bg-gray-100">
              <img src={p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => onRemove(i)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testId}-remove-photo-${i}`}>
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer hover:text-emerald-600 transition-colors mt-1">
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onUpload} data-testid={`${testId}-photo-input`} />
        {uploading ? (
          <div className="animate-spin h-3 w-3 border-2 border-emerald-600 rounded-full border-t-transparent" />
        ) : (
          <Camera className="w-3.5 h-3.5" />
        )}
        <span>Subir / Tomar foto</span>
      </label>
    </div>
  );
}

// ========== Dynamic List Component (with inline photos) ==========
function DynamicList({ items, onAdd, onRemove, onChange, placeholder, testId, photos, onPhotoUpload, onPhotoRemove, onPhotoReplace, uploadingPhoto }) {
  const [previewIdx, setPreviewIdx] = useState(null);
  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const itemPhotos = photos?.[idx] || [];
        const hasPhoto = itemPhotos.length > 0;
        return (
          <div key={idx} className="flex gap-2 items-center p-2 rounded-lg bg-gray-50 border border-gray-100">
            <Input
              value={item}
              onChange={(e) => onChange(idx, e.target.value)}
              placeholder={placeholder}
              className="flex-1"
              data-testid={`${testId}-item-${idx}`}
            />
            {/* Foto actions */}
            {onPhotoUpload && (
              hasPhoto ? (
                <Button variant="ghost" size="sm" onClick={() => setPreviewIdx(idx)} className="text-emerald-600 shrink-0" title="Ver foto" data-testid={`${testId}-${idx}-view-photo`}>
                  <Eye className="w-4 h-4" />
                </Button>
              ) : (
                <label className="shrink-0 cursor-pointer">
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPhotoUpload(idx, e)} data-testid={`${testId}-${idx}-photo-input`} />
                  {uploadingPhoto === idx ? (
                    <div className="animate-spin h-4 w-4 border-2 border-emerald-600 rounded-full border-t-transparent mx-2" />
                  ) : (
                    <div className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Tomar foto"><Camera className="w-4 h-4 text-gray-500" /></div>
                  )}
                </label>
              )
            )}
            <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="shrink-0" data-testid={`${testId}-remove-${idx}`}>
              <X className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={onAdd} data-testid={`${testId}-add`}>
        <Plus className="w-4 h-4 mr-1" /> Agregar ítem
      </Button>

      {/* Photo preview modal */}
      {previewIdx !== null && photos?.[previewIdx]?.length > 0 && (
        <PhotoPreviewModal
          photo={photos[previewIdx][0]}
          onClose={() => setPreviewIdx(null)}
          onReplace={onPhotoReplace ? (e) => { onPhotoReplace(previewIdx, e); setPreviewIdx(null); } : null}
          onDelete={() => { onPhotoRemove(previewIdx, 0); setPreviewIdx(null); }}
          testId={`${testId}-${previewIdx}`}
        />
      )}
    </div>
  );
}

// ========== Main Component ==========
export default function ActasVisitas({ user }) {
  const [view, setView] = useState('list'); // list | form | detail
  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [editingActa, setEditingActa] = useState(null);
  const [viewingActa, setViewingActa] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(null);

  // Email modal
  const [sendModal, setSendModal] = useState(null);
  const [emailList, setEmailList] = useState('');
  const [mensajeAdicional, setMensajeAdicional] = useState('');
  const [sending, setSending] = useState(false);

  // Filters
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  // Form state
  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    hora_inicio: '',
    hora_fin: '',
    ubicacion: '',
    finca: '',
    representante: user?.full_name || '',
    tecnico: '',
    empresa: 'PalmaTica',
    objetivos: [],
    objetivo_otro: '',
    documentos_entregados: [''],
    documentos_recibidos: [''],
    pendientes: '',
    observaciones: '',
    inspeccion: [],
    inspeccion_otro: '',
    hallazgos: '',
    estado_finca: 'conforme',
    incumplimientos_criticos: false,
    acciones_correctivas: [],
    firma_representante: null,
    firma_tecnico: null,
    evidencias: [],
    fotos_documentos_entregados: {},
    fotos_documentos_recibidos: {},
    fotos_hallazgos: [],
    fotos_observaciones: [],
    fotos_acta_fisica: [],
  });
  const [formStep, setFormStep] = useState(0);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [uploadingInlinePhoto, setUploadingInlinePhoto] = useState(null);

  // Email preview
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Stats públicas modal
  const [statsModal, setStatsModal] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const FORM_STEPS = [
    'Información General',
    'Documentos',
    'Inspección y Estado',
    'Acciones y Firmas',
  ];

  // ========== Data fetching ==========
  const fetchActas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFechaDesde) params.append('fecha_desde', filterFechaDesde);
      if (filterFechaHasta) params.append('fecha_hasta', filterFechaHasta);
      if (filterEstado) params.append('estado', filterEstado);
      const res = await axios.get(`${API}/actas-visitas?${params.toString()}`);
      setActas(res.data.actas || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Error al cargar actas');
    } finally {
      setLoading(false);
    }
  }, [filterFechaDesde, filterFechaHasta, filterEstado]);

  useEffect(() => {
    fetchActas();
  }, [fetchActas]);

  // ========== Form helpers ==========
  const resetForm = () => {
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      hora_inicio: '',
      hora_fin: '',
      ubicacion: '',
      finca: '',
      representante: user?.full_name || '',
      tecnico: '',
      empresa: 'PalmaTica',
      objetivos: [],
      objetivo_otro: '',
      documentos_entregados: [''],
      documentos_recibidos: [''],
      pendientes: '',
      observaciones: '',
      inspeccion: [],
      inspeccion_otro: '',
      hallazgos: '',
      estado_finca: 'conforme',
      incumplimientos_criticos: false,
      acciones_correctivas: [],
      firma_representante: null,
      firma_tecnico: null,
      evidencias: [],
      fotos_documentos_entregados: {},
      fotos_documentos_recibidos: {},
      fotos_hallazgos: [],
      fotos_observaciones: [],
      fotos_acta_fisica: [],
    });
    setFormStep(0);
  };

  const handleNewActa = () => {
    setEditingActa(null);
    resetForm();
    setView('form');
  };

  const handleEditActa = (acta) => {
    setEditingActa(acta);
    setForm({
      fecha: acta.fecha || '',
      hora_inicio: acta.hora_inicio || '',
      hora_fin: acta.hora_fin || '',
      ubicacion: acta.ubicacion || '',
      finca: acta.finca || '',
      representante: acta.representante || '',
      tecnico: acta.tecnico || '',
      empresa: acta.empresa || 'PalmaTica',
      objetivos: acta.objetivos || [],
      objetivo_otro: acta.objetivo_otro || '',
      documentos_entregados: acta.documentos_entregados?.length ? acta.documentos_entregados : [''],
      documentos_recibidos: acta.documentos_recibidos?.length ? acta.documentos_recibidos : [''],
      pendientes: acta.pendientes || '',
      observaciones: acta.observaciones || '',
      inspeccion: acta.inspeccion || [],
      inspeccion_otro: acta.inspeccion_otro || '',
      hallazgos: acta.hallazgos || '',
      estado_finca: acta.estado_finca || 'conforme',
      incumplimientos_criticos: acta.incumplimientos_criticos || false,
      acciones_correctivas: acta.acciones_correctivas || [],
      firma_representante: acta.firma_representante || null,
      firma_tecnico: acta.firma_tecnico || null,
      evidencias: acta.evidencias || [],
      fotos_documentos_entregados: acta.fotos_documentos_entregados || {},
      fotos_documentos_recibidos: acta.fotos_documentos_recibidos || {},
      fotos_hallazgos: acta.fotos_hallazgos || [],
      fotos_observaciones: acta.fotos_observaciones || [],
      fotos_acta_fisica: acta.fotos_acta_fisica || [],
    });
    setFormStep(0);
    setView('form');
  };

  const handleViewActa = async (acta) => {
    try {
      const res = await axios.get(`${API}/actas-visitas/${acta.id}`);
      setViewingActa(res.data);
      setView('detail');
    } catch {
      toast.error('Error al cargar acta');
    }
  };

  // ========== Save ==========
  const handleSave = async () => {
    if (!form.fecha || !form.tecnico) {
      toast.error('La fecha y el técnico visitante son requeridos');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        documentos_entregados: form.documentos_entregados.filter(d => d.trim()),
        documentos_recibidos: form.documentos_recibidos.filter(d => d.trim()),
      };

      if (editingActa) {
        await axios.put(`${API}/actas-visitas/${editingActa.id}?user_id=${user?.id}`, payload);
        toast.success('Acta actualizada exitosamente');
      } else {
        await axios.post(`${API}/actas-visitas?user_id=${user?.id}`, payload);
        toast.success('Acta creada exitosamente');
      }
      setView('list');
      fetchActas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar acta');
    } finally {
      setSaving(false);
    }
  };

  // ========== Delete ==========
  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await axios.delete(`${API}/actas-visitas/${deleteModal.id}`);
      toast.success('Acta eliminada');
      setDeleteModal(null);
      fetchActas();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  // ========== PDF ==========
  const handleDownloadPdf = async (acta) => {
    setGeneratingPdf(acta.id);
    try {
      const res = await axios.post(`${API}/actas-visitas/${acta.id}/pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${acta.fecha}_Acta_Visita_Tecnica_${acta.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('Error al generar PDF');
    } finally {
      setGeneratingPdf(null);
    }
  };

  // ========== Send Email ==========
  const handleOpenSendModal = (acta) => {
    setSendModal(acta);
    setEmailList('');
    const estado_map = { conforme: 'Conforme', conforme_observaciones: 'Conforme con observaciones', requiere_acciones: 'Requiere acciones correctivas' };
    const estadoTxt = estado_map[acta.estado_finca] || acta.estado_finca || '';
    setEmailSubject(`Acta de Visita Técnica ${acta.numero} — ${acta.finca || 'Finca'} (${acta.fecha})`);
    setEmailBody(
      `Estimado(a),\n\nSe adjunta copia del Acta de Visita Técnica ${acta.numero} correspondiente a la visita realizada el ${acta.fecha} a la finca ${acta.finca || ''}.\n\n` +
      `Datos de la visita:\n` +
      `- Fecha: ${acta.fecha}\n` +
      `- Finca: ${acta.finca || ''}\n` +
      `- Técnico visitante: ${acta.tecnico || ''} (${acta.empresa || ''})\n` +
      `- Representante: ${acta.representante || ''}\n` +
      `- Estado: ${estadoTxt}\n\n` +
      `El documento PDF se encuentra adjunto para su revisión y archivo.\nEste acta forma parte del sistema de trazabilidad y cumplimiento de la finca.\n\n` +
      `Atentamente,\nCiro del Pacífico — Sistema de Gestión Agrícola`
    );
  };

  const handleViewStats = async (acta) => {
    setStatsModal(acta);
    setStatsData(null);
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/actas-visitas/${acta.id}/stats-publicas`);
      setStatsData(res.data);
    } catch {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoadingStats(false);
    }
  };

  // Email-error modal (cuando Resend rechaza por dominio no verificado u otro error específico)
  const [emailErrorModal, setEmailErrorModal] = useState(null);

  const handleSendEmail = async () => {
    if (!sendModal || !emailList.trim()) {
      toast.error('Ingresa al menos un correo electrónico');
      return;
    }
    const emails = emailList.split(/[,;\n]+/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error('Ingresa al menos un correo válido');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/actas-visitas/${sendModal.id}/enviar`, {
        emails,
        asunto: emailSubject,
        cuerpo: emailBody,
      });
      const data = res.data;

      // Caso: ningún correo se envió → mostrar modal explicativo
      if (data.success === false || (data.enviados?.length === 0 && data.errores?.length > 0)) {
        const hasDomainErr = data.errores?.some(e => e.code === 'domain_not_verified');
        setEmailErrorModal({
          errores: data.errores || [],
          tipoPrincipal: hasDomainErr ? 'domain_not_verified' : 'other',
        });
        return;
      }

      // Éxito total o parcial
      toast.success(data.mensaje || `Acta enviada a ${data.enviados?.length || emails.length} destinatario(s)`);
      if (data.errores?.length) {
        const fallaDominio = data.errores.some(e => e.code === 'domain_not_verified');
        if (fallaDominio) {
          // Algunos sí, otros no → mostramos modal pero avisando que parte fue OK
          setEmailErrorModal({
            errores: data.errores,
            tipoPrincipal: 'domain_not_verified',
            enviados: data.enviados,
          });
        } else {
          toast.warning(`Falló el envío a: ${data.errores.map(e => e.email).join(', ')}`);
        }
      }
      if (data.enviados?.length === emails.length) setSendModal(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al enviar email');
    } finally {
      setSending(false);
    }
  };

  // ========== Inline photo upload (generic) ==========
  const handleInlinePhotoUpload = async (field, itemIdx, e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const key = `${field}-${itemIdx}`;
    setUploadingInlinePhoto(key);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true, fileType: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', compressed, compressed.name || file.name);
      formData.append('user_id', user?.id || '');
      formData.append('etiqueta', field.includes('entregados') ? 'documentos' : field.includes('recibidos') ? 'documentos' : 'campo');
      const res = await axios.post(`${API}/actas-visitas/upload-evidencia`, formData);
      if (res.data.success) {
        setForm(prev => {
          const photosMap = { ...(prev[field] || {}) };
          const arr = photosMap[itemIdx] || [];
          photosMap[itemIdx] = [...arr, { url: res.data.file_path, storage_type: res.data.storage_type }];
          return { ...prev, [field]: photosMap };
        });
        toast.success('Foto agregada');
      }
    } catch { toast.error('Error al subir foto'); }
    setUploadingInlinePhoto(null);
    e.target.value = '';
  };

  const handleInlinePhotoRemove = (field, itemIdx, photoIdx) => {
    setForm(prev => {
      const photosMap = { ...(prev[field] || {}) };
      const arr = [...(photosMap[itemIdx] || [])];
      arr.splice(photoIdx, 1);
      photosMap[itemIdx] = arr;
      return { ...prev, [field]: photosMap };
    });
  };

  // Replace an existing inline photo (reuses upload logic, clears old first)
  const handleInlinePhotoReplace = async (field, itemIdx, e) => {
    // Remove old photo(s) first
    setForm(prev => {
      const photosMap = { ...(prev[field] || {}) };
      photosMap[itemIdx] = [];
      return { ...prev, [field]: photosMap };
    });
    // Upload new one
    await handleInlinePhotoUpload(field, itemIdx, e);
  };

  // Upload for array-based photo fields (hallazgos, observaciones)
  const handleArrayPhotoUpload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingInlinePhoto(field);
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true, fileType: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', compressed, compressed.name || file.name);
      formData.append('user_id', user?.id || '');
      formData.append('etiqueta', field.includes('hallazgos') ? 'campo' : 'documentos');
      const res = await axios.post(`${API}/actas-visitas/upload-evidencia`, formData);
      if (res.data.success) {
        setForm(prev => ({ ...prev, [field]: [...(prev[field] || []), { url: res.data.file_path, storage_type: res.data.storage_type }] }));
        toast.success('Foto agregada');
      }
    } catch { toast.error('Error al subir foto'); }
    setUploadingInlinePhoto(null);
    e.target.value = '';
  };

  const handleArrayPhotoRemove = (field, photoIdx) => {
    setForm(prev => ({ ...prev, [field]: (prev[field] || []).filter((_, i) => i !== photoIdx) }));
  };

  // ========== Evidence upload ==========
  const handleUploadEvidence = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingEvidence(true);

    for (const file of files) {
      try {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} no es una imagen válida`);
          continue;
        }
        const compressed = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg',
        });
        const formData = new FormData();
        formData.append('file', compressed, compressed.name || file.name);
        formData.append('user_id', user?.id || '');
        formData.append('etiqueta', 'campo');

        const res = await axios.post(`${API}/actas-visitas/upload-evidencia`, formData);
        if (res.data.success) {
          setForm(prev => ({
            ...prev,
            evidencias: [...prev.evidencias, {
              url: res.data.file_path,
              etiqueta: res.data.etiqueta || 'campo',
              storage_type: res.data.storage_type,
            }],
          }));
          toast.success(`Foto subida: ${file.name}`);
        }
      } catch (err) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploadingEvidence(false);
    e.target.value = '';
  };

  const removeEvidence = (idx) => {
    setForm(prev => ({
      ...prev,
      evidencias: prev.evidencias.filter((_, i) => i !== idx),
    }));
  };

  // ========== Checkbox toggle ==========
  const toggleArrayItem = (field, item) => {
    setForm(prev => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item],
      };
    });
  };

  // ========== KPIs ==========
  const kpis = {
    total: total,
    conformes: actas.filter(a => a.estado_finca === 'conforme').length,
    observaciones: actas.filter(a => a.estado_finca === 'conforme_observaciones').length,
    acciones: actas.filter(a => a.estado_finca === 'requiere_acciones').length,
  };

  // ========== RENDER: LIST VIEW ==========
  const renderList = () => (
    <div className="space-y-6" data-testid="actas-list-view">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Actas</p>
            <p className="text-2xl font-bold text-blue-700" data-testid="kpi-total">{kpis.total}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Conformes</p>
            <p className="text-2xl font-bold text-emerald-700" data-testid="kpi-conformes">{kpis.conformes}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Con Observaciones</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="kpi-observaciones">{kpis.observaciones}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Acciones Correctivas</p>
            <p className="text-2xl font-bold text-red-700" data-testid="kpi-acciones">{kpis.acciones}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters + New button */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={filterFechaDesde} onChange={e => setFilterFechaDesde(e.target.value)} className="w-36" data-testid="filter-fecha-desde" />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={filterFechaHasta} onChange={e => setFilterFechaHasta(e.target.value)} className="w-36" data-testid="filter-fecha-hasta" />
          </div>
          <div>
            <Label className="text-xs">Estado</Label>
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="w-44" data-testid="filter-estado">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="conforme">Conforme</SelectItem>
                <SelectItem value="conforme_observaciones">Con observaciones</SelectItem>
                <SelectItem value="requiere_acciones">Acciones correctivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(filterFechaDesde || filterFechaHasta || filterEstado) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterFechaDesde(''); setFilterFechaHasta(''); setFilterEstado(''); }} data-testid="clear-filters">
              <X className="w-4 h-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
        <Button onClick={handleNewActa} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="new-acta-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Acta
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : actas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ClipboardCheck className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No hay actas registradas</h3>
            <p className="text-sm text-gray-400 mt-1">Crea tu primera acta de visita técnica</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No.</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="hidden sm:table-cell">Técnico</TableHead>
                  <TableHead className="hidden md:table-cell">Empresa</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actas.map(acta => (
                  <TableRow key={acta.id} data-testid={`acta-row-${acta.id}`}>
                    <TableCell className="font-mono text-sm font-medium">{acta.numero}</TableCell>
                    <TableCell>{acta.fecha}</TableCell>
                    <TableCell className="hidden sm:table-cell">{acta.tecnico}</TableCell>
                    <TableCell className="hidden md:table-cell">{acta.empresa}</TableCell>
                    <TableCell>
                      <Badge className={`${ESTADO_COLORS[acta.estado_finca] || 'bg-gray-100 text-gray-800'} text-xs`}>
                        {ESTADO_LABELS[acta.estado_finca] || acta.estado_finca}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleViewActa(acta)} data-testid={`view-acta-${acta.id}`} title="Ver">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditActa(acta)} data-testid={`edit-acta-${acta.id}`} title="Editar">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(acta)} disabled={generatingPdf === acta.id} data-testid={`pdf-acta-${acta.id}`} title="Descargar PDF">
                          {generatingPdf === acta.id ? <div className="animate-spin h-4 w-4 border-2 border-emerald-600 rounded-full border-t-transparent" /> : <Download className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenSendModal(acta)} data-testid={`send-acta-${acta.id}`} title="Enviar por correo">
                          <Send className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteModal(acta)} data-testid={`delete-acta-${acta.id}`} title="Eliminar">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Delete Modal moved to root return */}
    </div>
  );

  // ========== RENDER: FORM VIEW ==========
  const renderForm = () => (
    <div className="space-y-4" data-testid="actas-form-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setView('list')} data-testid="back-to-list">
            <ChevronLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h2 className="text-lg font-bold text-gray-800">
            {editingActa ? `Editar Acta ${editingActa.numero}` : 'Nueva Acta de Visita Técnica'}
          </h2>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex gap-1 overflow-x-auto pb-2">
        {FORM_STEPS.map((step, idx) => (
          <button
            key={idx}
            onClick={() => setFormStep(idx)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              formStep === idx
                ? 'bg-emerald-600 text-white'
                : formStep > idx
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-gray-100 text-gray-500'
            }`}
            data-testid={`form-step-${idx}`}
          >
            {idx + 1}. {step}
          </button>
        ))}
      </div>

      {/* Step content */}
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-5">
          <AnimatePresence mode="wait">
            <motion.div
              key={formStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {formStep === 0 && renderStep0()}
              {formStep === 1 && renderStep1()}
              {formStep === 2 && renderStep2()}
              {formStep === 3 && renderStep3()}
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => setFormStep(Math.max(0, formStep - 1))}
          disabled={formStep === 0}
          data-testid="form-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>
        <span className="text-sm text-gray-500">{formStep + 1} / {FORM_STEPS.length}</span>
        {formStep < FORM_STEPS.length - 1 ? (
          <Button onClick={() => setFormStep(formStep + 1)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="form-next">
            Siguiente <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="form-save">
            {saving ? 'Guardando...' : (editingActa ? 'Guardar Cambios' : 'Cerrar Acta')}
          </Button>
        )}
      </div>
    </div>
  );

  // ========== STEP 0: General Info ==========
  const renderStep0 = () => (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-700 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-emerald-600" /> Información General
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Fecha de visita *</Label>
          <Input type="date" value={form.fecha} onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} data-testid="field-fecha" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Hora inicio</Label>
            <Input type="time" value={form.hora_inicio} onChange={e => setForm(p => ({ ...p, hora_inicio: e.target.value }))} data-testid="field-hora-inicio" />
          </div>
          <div>
            <Label>Hora fin</Label>
            <Input type="time" value={form.hora_fin} onChange={e => setForm(p => ({ ...p, hora_fin: e.target.value }))} data-testid="field-hora-fin" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Ubicación</Label>
          <Input value={form.ubicacion} onChange={e => setForm(p => ({ ...p, ubicacion: e.target.value }))} placeholder="Ej: Finca Ciro del Pacífico" data-testid="field-ubicacion" />
        </div>
        <div>
          <Label>Finca</Label>
          <Input value={form.finca} onChange={e => setForm(p => ({ ...p, finca: e.target.value }))} placeholder="Nombre de la finca" data-testid="field-finca" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Representante de la finca</Label>
          <Input value={form.representante} onChange={e => setForm(p => ({ ...p, representante: e.target.value }))} data-testid="field-representante" />
        </div>
        <div>
          <Label>Técnico visitante *</Label>
          <Input value={form.tecnico} onChange={e => setForm(p => ({ ...p, tecnico: e.target.value }))} placeholder="Nombre del técnico" data-testid="field-tecnico" />
        </div>
      </div>
      <div>
        <Label>Empresa visitante</Label>
        <Input value={form.empresa} onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))} data-testid="field-empresa" />
      </div>

      {/* Objetivos */}
      <div>
        <Label className="mb-2 block">Objetivo de la visita</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OBJETIVOS_OPTIONS.map(obj => (
            <label key={obj} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
              <Checkbox
                checked={form.objetivos.includes(obj)}
                onCheckedChange={() => toggleArrayItem('objetivos', obj)}
                data-testid={`obj-${obj.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <span className="text-sm">{obj}</span>
            </label>
          ))}
        </div>
        <div className="mt-2">
          <Input
            value={form.objetivo_otro}
            onChange={e => setForm(p => ({ ...p, objetivo_otro: e.target.value }))}
            placeholder="Otro objetivo..."
            data-testid="field-objetivo-otro"
          />
        </div>
      </div>
    </div>
  );

  // ========== STEP 1: Documents & Observations ==========
  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-emerald-600" /> Documentos entregados por la finca
        </h3>
        <DynamicList
          items={form.documentos_entregados}
          onAdd={() => setForm(p => ({ ...p, documentos_entregados: [...p.documentos_entregados, ''] }))}
          onRemove={(idx) => setForm(p => ({ ...p, documentos_entregados: p.documentos_entregados.filter((_, i) => i !== idx) }))}
          onChange={(idx, val) => setForm(p => {
            const arr = [...p.documentos_entregados];
            arr[idx] = val;
            return { ...p, documentos_entregados: arr };
          })}
          placeholder="Nombre del documento"
          testId="docs-entregados"
          photos={form.fotos_documentos_entregados}
          onPhotoUpload={(idx, e) => handleInlinePhotoUpload('fotos_documentos_entregados', idx, e)}
          onPhotoRemove={(idx, photoIdx) => handleInlinePhotoRemove('fotos_documentos_entregados', idx, photoIdx)}
          onPhotoReplace={(idx, e) => handleInlinePhotoReplace('fotos_documentos_entregados', idx, e)}
          uploadingPhoto={uploadingInlinePhoto?.startsWith('fotos_documentos_entregados-') ? parseInt(uploadingInlinePhoto.split('-').pop()) : null}
        />
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-blue-600" /> Documentos recibidos
        </h3>
        <DynamicList
          items={form.documentos_recibidos}
          onAdd={() => setForm(p => ({ ...p, documentos_recibidos: [...p.documentos_recibidos, ''] }))}
          onRemove={(idx) => setForm(p => ({ ...p, documentos_recibidos: p.documentos_recibidos.filter((_, i) => i !== idx) }))}
          onChange={(idx, val) => setForm(p => {
            const arr = [...p.documentos_recibidos];
            arr[idx] = val;
            return { ...p, documentos_recibidos: arr };
          })}
          placeholder="Nombre del documento"
          testId="docs-recibidos"
          photos={form.fotos_documentos_recibidos}
          onPhotoUpload={(idx, e) => handleInlinePhotoUpload('fotos_documentos_recibidos', idx, e)}
          onPhotoRemove={(idx, photoIdx) => handleInlinePhotoRemove('fotos_documentos_recibidos', idx, photoIdx)}
          onPhotoReplace={(idx, e) => handleInlinePhotoReplace('fotos_documentos_recibidos', idx, e)}
          uploadingPhoto={uploadingInlinePhoto?.startsWith('fotos_documentos_recibidos-') ? parseInt(uploadingInlinePhoto.split('-').pop()) : null}
        />
      </div>

      <div>
        <Label>Documentos pendientes</Label>
        <Textarea
          value={form.pendientes}
          onChange={e => setForm(p => ({ ...p, pendientes: e.target.value }))}
          placeholder="Documentos que quedaron pendientes de entrega..."
          rows={3}
          data-testid="field-pendientes"
        />
      </div>

      <div>
        <Label>Observaciones generales</Label>
        <Textarea
          value={form.observaciones}
          onChange={e => setForm(p => ({ ...p, observaciones: e.target.value }))}
          placeholder="Observaciones adicionales de la visita..."
          rows={3}
          data-testid="field-observaciones"
        />
        <InlinePhotoUpload
          onUpload={(e) => handleArrayPhotoUpload('fotos_observaciones', e)}
          uploading={uploadingInlinePhoto === 'fotos_observaciones'}
          photos={form.fotos_observaciones}
          onRemove={(photoIdx) => handleArrayPhotoRemove('fotos_observaciones', photoIdx)}
          testId="obs"
        />
      </div>
    </div>
  );

  // ========== STEP 2: Inspection & Status ==========
  const renderStep2 = () => (
    <div className="space-y-5">
      {/* Inspección de campo */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-emerald-600" /> Inspección de campo
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INSPECCION_OPTIONS.map(item => (
            <label key={item} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
              <Checkbox
                checked={form.inspeccion.includes(item)}
                onCheckedChange={() => toggleArrayItem('inspeccion', item)}
                data-testid={`insp-${item.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <span className="text-sm">{item}</span>
            </label>
          ))}
        </div>
        <Input
          value={form.inspeccion_otro}
          onChange={e => setForm(p => ({ ...p, inspeccion_otro: e.target.value }))}
          placeholder="Otro..."
          className="mt-2"
          data-testid="field-inspeccion-otro"
        />
      </div>

      <div>
        <Label>Hallazgos relevantes</Label>
        <Textarea
          value={form.hallazgos}
          onChange={e => setForm(p => ({ ...p, hallazgos: e.target.value }))}
          placeholder="Describe los hallazgos encontrados durante la inspección..."
          rows={3}
          data-testid="field-hallazgos"
        />
        <InlinePhotoUpload
          onUpload={(e) => handleArrayPhotoUpload('fotos_hallazgos', e)}
          uploading={uploadingInlinePhoto === 'fotos_hallazgos'}
          photos={form.fotos_hallazgos}
          onRemove={(photoIdx) => handleArrayPhotoRemove('fotos_hallazgos', photoIdx)}
          testId="hallazgos"
        />
      </div>

      {/* Estado */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-3">Estado de la finca</h3>
        <RadioGroup
          value={form.estado_finca}
          onValueChange={val => setForm(p => ({ ...p, estado_finca: val }))}
          className="space-y-2"
        >
          <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-emerald-50 transition-colors">
            <RadioGroupItem value="conforme" data-testid="estado-conforme" />
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium">Conforme</span>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-amber-50 transition-colors">
            <RadioGroupItem value="conforme_observaciones" data-testid="estado-observaciones" />
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium">Conforme con observaciones</span>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-red-50 transition-colors">
            <RadioGroupItem value="requiere_acciones" data-testid="estado-acciones" />
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium">Requiere acciones correctivas</span>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* Incumplimientos críticos */}
      <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-red-50 transition-colors">
        <Checkbox
          checked={form.incumplimientos_criticos}
          onCheckedChange={val => setForm(p => ({ ...p, incumplimientos_criticos: !!val }))}
          data-testid="field-incumplimientos"
        />
        <span className="text-sm font-medium text-red-700">Se detectaron incumplimientos críticos</span>
      </label>
    </div>
  );

  // ========== STEP 3: Actions, Signatures, Evidence ==========
  const renderStep3 = () => (
    <div className="space-y-5">
      {/* Acciones correctivas */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-3">Acciones correctivas</h3>
        {form.acciones_correctivas.length > 0 && (
          <div className="space-y-2 mb-3">
            {form.acciones_correctivas.map((ac, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg">
                <Input
                  value={ac.accion}
                  onChange={e => {
                    const arr = [...form.acciones_correctivas];
                    arr[idx] = { ...arr[idx], accion: e.target.value };
                    setForm(p => ({ ...p, acciones_correctivas: arr }));
                  }}
                  placeholder="Acción"
                  data-testid={`accion-${idx}-text`}
                />
                <Input
                  value={ac.responsable}
                  onChange={e => {
                    const arr = [...form.acciones_correctivas];
                    arr[idx] = { ...arr[idx], responsable: e.target.value };
                    setForm(p => ({ ...p, acciones_correctivas: arr }));
                  }}
                  placeholder="Responsable"
                  data-testid={`accion-${idx}-resp`}
                />
                <Input
                  type="date"
                  value={ac.fecha_compromiso}
                  onChange={e => {
                    const arr = [...form.acciones_correctivas];
                    arr[idx] = { ...arr[idx], fecha_compromiso: e.target.value };
                    setForm(p => ({ ...p, acciones_correctivas: arr }));
                  }}
                  data-testid={`accion-${idx}-fecha`}
                />
                <Button variant="ghost" size="sm" onClick={() => setForm(p => ({
                  ...p,
                  acciones_correctivas: p.acciones_correctivas.filter((_, i) => i !== idx)
                }))} data-testid={`accion-${idx}-remove`}>
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={() => setForm(p => ({
          ...p,
          acciones_correctivas: [...p.acciones_correctivas, { accion: '', responsable: '', fecha_compromiso: '' }]
        }))} data-testid="add-accion-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar fila
        </Button>
      </div>

      {/* Firmas */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Pen className="w-4 h-4 text-emerald-600" /> Firma Digital
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SignaturePad
            label="Firma del representante"
            value={form.firma_representante}
            onChange={val => setForm(p => ({ ...p, firma_representante: val }))}
            testId="firma-representante"
          />
          <SignaturePad
            label="Firma del técnico visitante"
            value={form.firma_tecnico}
            onChange={val => setForm(p => ({ ...p, firma_tecnico: val }))}
            testId="firma-tecnico"
          />
        </div>
      </div>

      {/* Evidencia fotográfica */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Camera className="w-4 h-4 text-emerald-600" /> Evidencia fotográfica
        </h3>
        <div className="flex flex-wrap gap-3 mb-3">
          {form.evidencias.map((ev, idx) => (
            <div key={idx} className="relative group w-24 h-24 rounded-lg overflow-hidden border bg-gray-100">
              <img src={ev.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${ev.url}` : ev.url} alt={`Evidencia ${idx + 1}`} className="w-full h-full object-cover" />
              <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => removeEvidence(idx)} className="bg-red-500 text-white rounded-full p-1 m-1" data-testid={`remove-evidence-${idx}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">
                {ev.etiqueta}
              </div>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUploadEvidence}
            className="hidden"
            data-testid="upload-evidence-input"
          />
          {uploadingEvidence ? (
            <div className="animate-spin h-4 w-4 border-2 border-emerald-600 rounded-full border-t-transparent" />
          ) : (
            <Upload className="w-4 h-4 text-gray-500" />
          )}
          <span className="text-sm text-gray-600">Subir fotos</span>
        </label>
      </div>

      {/* Foto del acta física de PalmaTica */}
      <div>
        <h3 className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-600" /> Foto del acta física
        </h3>
        <p className="text-xs text-gray-500 mb-3">Fotografía el acta que llena el personero de PalmaTica para que quede en nuestro registro.</p>
        <div className="flex flex-wrap gap-3 mb-3">
          {form.fotos_acta_fisica.map((p, idx) => (
            <div key={idx} className="relative group w-28 h-28 rounded-lg overflow-hidden border bg-gray-100">
              <img src={p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt={`Acta física ${idx + 1}`} className="w-full h-full object-cover" />
              <button onClick={() => handleArrayPhotoRemove('fotos_acta_fisica', idx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 m-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-acta-fisica-${idx}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50 transition-colors">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleArrayPhotoUpload('fotos_acta_fisica', e)}
            className="hidden"
            data-testid="upload-acta-fisica-input"
          />
          {uploadingInlinePhoto === 'fotos_acta_fisica' ? (
            <div className="animate-spin h-4 w-4 border-2 border-amber-600 rounded-full border-t-transparent" />
          ) : (
            <Camera className="w-4 h-4 text-amber-600" />
          )}
          <span className="text-sm text-amber-700 font-medium">Subir / Tomar foto del acta</span>
        </label>
      </div>
    </div>
  );

  // ========== RENDER: DETAIL VIEW ==========
  const renderDetail = () => {
    if (!viewingActa) return null;
    const a = viewingActa;
    return (
      <div className="space-y-4" data-testid="actas-detail-view">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setView('list'); setViewingActa(null); }} data-testid="detail-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
            <h2 className="text-lg font-bold text-gray-800">Acta {a.numero}</h2>
            <Badge className={`${ESTADO_COLORS[a.estado_finca] || ''} text-xs`}>
              {ESTADO_LABELS[a.estado_finca] || a.estado_finca}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleEditActa(a)} data-testid="detail-edit-btn">
              <Edit className="w-4 h-4 mr-1" /> Editar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(a)} disabled={generatingPdf === a.id} data-testid="detail-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleOpenSendModal(a)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="detail-send-btn">
              <Send className="w-4 h-4 mr-1" /> Enviar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleViewStats(a)} className="text-purple-700 border-purple-200 hover:bg-purple-50" data-testid="detail-stats-btn">
              <BarChart3 className="w-4 h-4 mr-1" />
              Vistas
              {(a.vistas_publicas ?? 0) > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[10px] font-bold" data-testid="detail-stats-count">
                  {a.vistas_publicas}
                </span>
              )}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-4 sm:p-6 space-y-6">
            {/* Info general */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Fecha" value={a.fecha} />
              <InfoRow icon={<Clock className="w-4 h-4" />} label="Horario" value={`${a.hora_inicio || '—'} - ${a.hora_fin || '—'}`} />
              <InfoRow icon={<MapPin className="w-4 h-4" />} label="Ubicación" value={a.ubicacion} />
              <InfoRow icon={<Building2 className="w-4 h-4" />} label="Finca" value={a.finca} />
              <InfoRow icon={<User className="w-4 h-4" />} label="Representante" value={a.representante} />
              <InfoRow icon={<User className="w-4 h-4" />} label="Técnico" value={`${a.tecnico} (${a.empresa})`} />
            </div>

            {/* Objetivos */}
            {(a.objetivos?.length > 0 || a.objetivo_otro) && (
              <Section title="Objetivos de la visita">
                <div className="flex flex-wrap gap-2">
                  {a.objetivos?.map((o, i) => <Badge key={i} variant="outline" className="text-sm">{o}</Badge>)}
                  {a.objetivo_otro && <Badge variant="outline" className="text-sm">{a.objetivo_otro}</Badge>}
                </div>
              </Section>
            )}

            {/* Documentos */}
            {a.documentos_entregados?.length > 0 && (
              <Section title="Documentos entregados por la finca">
                <DocListWithPhotos docs={a.documentos_entregados} photos={a.fotos_documentos_entregados} testId="detail-docs-entregados" />
              </Section>
            )}
            {a.documentos_recibidos?.length > 0 && (
              <Section title="Documentos recibidos">
                <DocListWithPhotos docs={a.documentos_recibidos} photos={a.fotos_documentos_recibidos} testId="detail-docs-recibidos" />
              </Section>
            )}

            {/* Observaciones */}
            {(a.pendientes || a.observaciones) && (
              <Section title="Observaciones">
                {a.pendientes && <p className="text-sm"><strong>Pendientes:</strong> {a.pendientes}</p>}
                {a.observaciones && <p className="text-sm mt-1"><strong>Generales:</strong> {a.observaciones}</p>}
                {a.fotos_observaciones?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {a.fotos_observaciones.map((p, i) => (
                      <img key={i} src={p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt="" className="w-20 h-20 rounded border object-cover" />
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Inspección */}
            {(a.inspeccion?.length > 0 || a.hallazgos) && (
              <Section title="Inspección de campo">
                {a.inspeccion?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {a.inspeccion.map((item, i) => <Badge key={i} variant="secondary" className="text-xs">{item}</Badge>)}
                    {a.inspeccion_otro && <Badge variant="secondary" className="text-xs">{a.inspeccion_otro}</Badge>}
                  </div>
                )}
                {a.hallazgos && <p className="text-sm"><strong>Hallazgos:</strong> {a.hallazgos}</p>}
                {a.fotos_hallazgos?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {a.fotos_hallazgos.map((p, i) => (
                      <img key={i} src={p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt="" className="w-20 h-20 rounded border object-cover" />
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Estado */}
            <Section title="Estado de la finca">
              <div className="flex items-center gap-3">
                <Badge className={`${ESTADO_COLORS[a.estado_finca] || ''} px-3 py-1`}>
                  {ESTADO_LABELS[a.estado_finca] || a.estado_finca}
                </Badge>
                {a.incumplimientos_criticos && (
                  <Badge className="bg-red-600 text-white px-3 py-1">Incumplimientos críticos detectados</Badge>
                )}
              </div>
            </Section>

            {/* Acciones correctivas */}
            {a.acciones_correctivas?.length > 0 && (
              <Section title="Acciones correctivas">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Acción</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead>Fecha compromiso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {a.acciones_correctivas.map((ac, i) => (
                      <TableRow key={i}>
                        <TableCell>{ac.accion}</TableCell>
                        <TableCell>{ac.responsable}</TableCell>
                        <TableCell>{ac.fecha_compromiso}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
            )}

            {/* Firmas */}
            {(a.firma_representante || a.firma_tecnico) && (
              <Section title="Firmas">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {a.firma_representante && (
                    <div className="text-center">
                      <img src={a.firma_representante} alt="Firma representante" className="max-h-24 mx-auto border rounded p-2 bg-white" />
                      <p className="text-xs text-gray-500 mt-1">Representante: {a.representante}</p>
                    </div>
                  )}
                  {a.firma_tecnico && (
                    <div className="text-center">
                      <img src={a.firma_tecnico} alt="Firma técnico" className="max-h-24 mx-auto border rounded p-2 bg-white" />
                      <p className="text-xs text-gray-500 mt-1">Técnico: {a.tecnico}</p>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Evidencias */}
            {a.evidencias?.length > 0 && (
              <Section title="Evidencia fotográfica">
                <div className="flex flex-wrap gap-3">
                  {a.evidencias.map((ev, idx) => (
                    <div key={idx} className="relative w-28 h-28 rounded-lg overflow-hidden border bg-gray-100">
                      <img src={ev.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${ev.url}` : ev.url} alt={`Evidencia ${idx + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">{ev.etiqueta}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Foto del acta física */}
            {a.fotos_acta_fisica?.length > 0 && (
              <Section title="Foto del acta física (PalmaTica)">
                <div className="flex flex-wrap gap-3">
                  {a.fotos_acta_fisica.map((p, idx) => (
                    <div key={idx} className="relative w-28 h-28 rounded-lg overflow-hidden border bg-gray-100">
                      <img src={p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url} alt={`Acta física ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Trazabilidad */}
            <div className="border-t pt-4 mt-4">
              <p className="text-xs text-gray-400">
                Creado por: {a.created_by} | Creado: {a.created_at?.slice(0, 10)} | Última modificación: {a.updated_at?.slice(0, 10)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <ProduccionLayout user={user}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {view === 'list' && renderList()}
        {view === 'form' && renderForm()}
        {view === 'detail' && renderDetail()}
      </div>

      {/* Delete Modal */}
      <Dialog open={!!deleteModal} onOpenChange={() => setDeleteModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Acta</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            ¿Estás seguro de eliminar el acta <strong>{deleteModal?.numero}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(null)} data-testid="cancel-delete">Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal - Preview Editable */}
      <Dialog open={!!sendModal} onOpenChange={() => setSendModal(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Vista previa del correo — {sendModal?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Destinatarios *</Label>
              <Textarea
                value={emailList}
                onChange={e => setEmailList(e.target.value)}
                placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
                rows={2}
                className="mt-1"
                data-testid="send-email-list"
              />
              <p className="text-xs text-gray-400 mt-1">Separa múltiples correos con coma o salto de línea</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Asunto</Label>
              <Input
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
                className="mt-1"
                data-testid="send-email-subject"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Contenido del correo</Label>
              <Textarea
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
                rows={12}
                className="mt-1 font-mono text-sm"
                data-testid="send-email-body"
              />
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
              <FileText className="w-4 h-4 shrink-0" />
              <span>Se adjuntará: <strong>{sendModal?.fecha}_Acta_Visita_Tecnica_{sendModal?.numero}.pdf</strong></span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModal(null)} data-testid="cancel-send">Cancelar</Button>
            <Button onClick={handleSendEmail} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-send">
              {sending ? (
                <><div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Enviar correo</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: estadísticas de vistas públicas */}
      <Dialog open={!!statsModal} onOpenChange={(o) => !o && setStatsModal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="stats-publicas-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-700" />
              Vistas públicas — Acta {statsModal?.numero}
            </DialogTitle>
          </DialogHeader>
          {loadingStats ? (
            <div className="py-8 text-center text-sm text-gray-500">Cargando estadísticas…</div>
          ) : statsData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-purple-700 font-medium">Total de vistas</p>
                  <p className="text-2xl font-bold text-purple-900" data-testid="stats-total-vistas">{statsData.total_vistas || 0}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-medium">Última vista</p>
                  <p className="text-sm font-semibold text-emerald-900 mt-1.5" data-testid="stats-ultima-vista">
                    {statsData.ultima_vista
                      ? new Date(statsData.ultima_vista).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' })
                      : 'Aún sin vistas'}
                  </p>
                </div>
              </div>

              {statsData.log?.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Historial reciente (últimas {statsData.log.length})</p>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {statsData.log.map((entry, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start justify-between gap-3" data-testid={`stats-log-row-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium">
                            {new Date(entry.viewed_at).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' })}
                          </p>
                          <p className="text-gray-500 truncate">{entry.ua || 'Navegador desconocido'}</p>
                        </div>
                        <span className="text-gray-400 font-mono text-[10px] shrink-0">{entry.ip_anon || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <Eye className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Aún nadie ha escaneado el QR de esta acta</p>
                  <p className="text-xs text-gray-400 mt-1">Cuando alguien acceda a la vista pública aparecerá aquí.</p>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setStatsModal(null)} data-testid="stats-modal-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal: error de envío por dominio no verificado u otro problema Resend */}
      <Dialog open={!!emailErrorModal} onOpenChange={(o) => !o && setEmailErrorModal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="email-error-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              {emailErrorModal?.tipoPrincipal === 'domain_not_verified'
                ? 'Falta verificar tu dominio en Resend'
                : 'No se pudo enviar el correo'}
            </DialogTitle>
          </DialogHeader>

          {emailErrorModal?.tipoPrincipal === 'domain_not_verified' ? (
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                Tu cuenta de <strong>Resend</strong> aún no tiene un dominio verificado, por eso solo puede
                enviar correos a <strong>tu propio email</strong> (el que registraste en Resend) o al sandbox
                <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs ml-1">delivered@resend.dev</code>.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="font-semibold text-emerald-900 mb-2">¿Cómo arreglarlo? (5 minutos)</p>
                <ol className="list-decimal ml-5 space-y-1 text-sm text-emerald-900">
                  <li>Inicia sesión en <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-emerald-700 underline font-medium">resend.com/domains</a></li>
                  <li>Pulsa <strong>"Add Domain"</strong> y agrega <code className="px-1 bg-white rounded">cirodelpacifico.com</code></li>
                  <li>Copia los registros DNS (SPF/DKIM) que te muestre Resend</li>
                  <li>Pégalos en el panel DNS de tu proveedor (GoDaddy, Cloudflare, etc.)</li>
                  <li>Vuelve a Resend y pulsa <strong>"Verify"</strong>. Listo.</li>
                </ol>
              </div>
              <p className="text-xs text-gray-500">
                Una vez verificado, podrás enviar a cualquier dirección. Mientras tanto, prueba enviando a
                tu propio correo <code className="px-1 bg-gray-100 rounded">admin@cirodelpacifico.com</code>.
              </p>
              {emailErrorModal?.enviados?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900 text-xs">
                  ✅ <strong>{emailErrorModal.enviados.length}</strong> correo(s) sí se enviaron correctamente: {emailErrorModal.enviados.join(', ')}
                </div>
              )}
              {emailErrorModal?.errores?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">No se pudo enviar a:</p>
                  <ul className="text-xs bg-red-50 border border-red-200 rounded-lg p-2 space-y-0.5">
                    {emailErrorModal.errores.map((e, i) => (
                      <li key={i} className="text-red-800">• {e.email}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-gray-700">No se pudo enviar el correo a los siguientes destinatarios:</p>
              <ul className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                {emailErrorModal?.errores?.map((e, i) => (
                  <li key={i} className="text-xs">
                    <p className="font-semibold text-red-900">{e.email}</p>
                    <p className="text-red-700 mt-0.5 break-words">{e.error}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            {emailErrorModal?.tipoPrincipal === 'domain_not_verified' && (
              <a
                href="https://resend.com/domains"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800"
                data-testid="email-error-resend-link"
              >
                Abrir Resend
              </a>
            )}
            <Button variant="outline" size="sm" onClick={() => setEmailErrorModal(null)} data-testid="email-error-close">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </ProduccionLayout>
  );
}

// ========== Helper Components ==========
function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">{title}</h4>
      {children}
    </div>
  );
}

// ========== Document list with eye icon for photos (detail view) ==========
function DocListWithPhotos({ docs, photos, testId }) {
  const [previewPhoto, setPreviewPhoto] = useState(null);
  return (
    <>
      <ul className="space-y-1.5 text-sm">
        {docs.map((d, i) => {
          const docPhotos = photos?.[String(i)] || [];
          const hasPhoto = docPhotos.length > 0;
          return (
            <li key={i} className="flex items-center gap-2">
              <span className="flex-1">{d}</span>
              {hasPhoto && (
                <button
                  onClick={() => setPreviewPhoto(docPhotos[0])}
                  className="text-emerald-600 hover:text-emerald-800 transition-colors p-1 rounded hover:bg-emerald-50"
                  title="Ver foto"
                  data-testid={`${testId}-${i}-view-photo`}
                >
                  <Eye className="w-4 h-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {previewPhoto && (
        <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Foto del documento</DialogTitle></DialogHeader>
            <div className="flex justify-center bg-gray-50 rounded-lg p-2">
              <img
                src={previewPhoto.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${previewPhoto.url}` : previewPhoto.url}
                alt="Foto documento"
                className="max-h-80 rounded object-contain"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setPreviewPhoto(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
